import React from 'react';
import { Drawer, IconButton, Typography, Box, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

interface ExcelUploadDrawerProps {
  open: boolean;
  onClose: () => void;
  uploadStatus: 'idle' | 'loading' | 'success' | 'error';
  exportStatus: 'idle' | 'loading' | 'success' | 'error';
  onDownloadTemplate: () => void;
  onUploadExcel: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExportExcel: () => void;
  configId?: string;
  excelUploaded: boolean;
  uploadedFileName?: string;
}

const ExcelUploadDrawer: React.FC<ExcelUploadDrawerProps> = ({
  open,
  onClose,
  uploadStatus,
  exportStatus,
  onDownloadTemplate,
  onUploadExcel,
  onExportExcel,
  configId,
  excelUploaded,
  uploadedFileName,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '400px',
          padding: '30px',
        },
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
            Upload Excel Options
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Download Template */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Step 1: Download Template
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={onDownloadTemplate}
            >
              Download Template
            </Button>
          </Box>

          {/* Upload Excel */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Step 2: Upload Excel
            </Typography>
            <Button
              fullWidth
              component="label"
              variant="contained"
              startIcon={uploadStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              disabled={uploadStatus === 'loading'}
            >
              {uploadStatus === 'loading' ? 'Uploading...' : 'Upload Excel'}
              <input
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={onUploadExcel}
                hidden
              />
            </Button>
            {uploadStatus === 'success' && uploadedFileName && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  backgroundColor: '#e8f5e9',
                  borderRadius: '4px',
                  border: '1px solid #4caf50',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#2e7d32',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  ✓ Uploaded: <span style={{ fontWeight: 600 }}>{uploadedFileName}</span>
                </Typography>
              </Box>
            )}
          </Box>

          {/* Export Excel */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Step 3: Export Options (Optional)
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              startIcon={exportStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
              onClick={onExportExcel}
              disabled={exportStatus === 'loading' || !excelUploaded}
            >
              {exportStatus === 'loading' ? 'Exporting...' : 'Export Excel'}
            </Button>
            {!excelUploaded && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'orange',
                  mt: 1,
                }}
              >
                Upload excel first to enable export
              </Typography>
            )}
          </Box>
        </Box>

        <Box mt={4}>
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              color: '#666',
            }}
          >
            Note: Upload the Excel file with options. You can export to get pre-filled IDs. Modify existing rows to update, or add new rows to create new options.
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ExcelUploadDrawer;
